import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { formatDateUK } from '../utils/dateUtils';
import { handleApiError } from '../utils/errorHandler';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Users as UsersIcon,
  X,
  Eye,
  EyeOff,
  Shield,
  UserCheck,
  UserX,
  Key,
  Building,
  UserCog,
} from 'lucide-react';
import { usersAPI } from '../utils/api';
import { toast } from 'react-toastify';
import { usePermissions } from '../hooks/usePermissions';
import PermissionGuard from '../components/PermissionGuard';
import { useDispatch } from 'react-redux';
import { impersonate, reset } from '../store/slices/authSlice';

const userSchema = yup.object({
  name: yup.string().required('Name is required').min(2, 'Name must be at least 2 characters'),
  email: yup.string().email('Invalid email').required('Email is required'),
  companyName: yup
    .string()
    .required('Company name is required')
    .min(2, 'Company name must be at least 2 characters'),
  password: yup.string().when('isEdit', ([isEdit], schema) => {
    return isEdit === false
      ? schema.required('Password is required').min(6, 'Password must be at least 6 characters')
      : schema.nullable();
  }),
  role: yup
    .string()
    .required('Role is required')
    .oneOf(['super-admin', 'admin', 'bookings', 'view-only'], 'Invalid role')
    .test('role-hierarchy', 'You can only create users with lower roles', function (value) {
      // This will be validated on the backend, but we can add client-side validation too
      return true; // Let backend handle the detailed validation
    }),
});

const resetPasswordSchema = yup.object({
  newPassword: yup
    .string()
    .required('New password is required')
    .min(6, 'Password must be at least 6 characters'),
});

const companyStatusSchema = yup.object({
  companyStatus: yup
    .string()
    .required('Company status is required')
    .oneOf(['disabled', 'trial', 'active'], 'Invalid status'),
});

const UserModal = ({ user, onClose, onSave, isEdit = false }) => {
  const [showPassword, setShowPassword] = useState(false);
  const { isSuperAdmin, user: currentUser } = usePermissions();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(userSchema),
    defaultValues: user || { role: 'bookings' },
    context: { isEdit },
  });

  useEffect(() => {
    if (user) {
      reset({
        name: user.name,
        email: user.email,
        companyName: user.companyName,
        role: user.role,
        isActive: user.isActive,
      });
    } else if (currentUser?.role !== 'super-admin' && currentUser?.companyName) {
      // For admins creating new users, pre-fill with their company name
      reset({
        role: 'admin',
        companyName: currentUser.companyName,
      });
    }
  }, [user, reset, currentUser]);

  const onSubmit = data => {
    if (isEdit) {
      // Remove password field if empty for edit
      if (!data.password) {
        delete data.password;
      }
    }
    onSave(data);
  };

  // Role options based on current user's role
  const roleOptions = isSuperAdmin()
    ? [
        {
          value: 'super-admin',
          label: 'Super Admin',
          description: 'Full system access and company management',
        },
        { value: 'admin', label: 'Admin', description: 'Full access to all features' },
        {
          value: 'bookings',
          label: 'Bookings and View',
          description: 'Can add bookings and view reports',
        },
        {
          value: 'view-only',
          label: 'Artwork Only',
          description: 'Can only view artwork, create the advertiser index, and export',
        },
      ]
    : currentUser?.role === 'admin'
      ? [
        { value: 'admin', label: 'Admin', description: 'Full access to all features' },
          {
            value: 'bookings',
            label: 'Bookings and View',
            description: 'Can add bookings and view reports',
          },
          {
            value: 'view-only',
            label: 'Artwork Only',
            description: 'Can only view artwork, create the advertiser index, and export',
          },
        ]
      : [
          // Other roles shouldn't be able to create users, but just in case
          {
            value: 'bookings',
            label: 'Bookings and View',
            description: 'Can add bookings and view reports',
          },
          {
            value: 'view-only',
            label: 'Artwork Only',
            description: 'Can only view artwork, create the advertiser index, and export',
          },
        ];

  return (
    <div className="flex overflow-y-auto fixed inset-0 z-50 justify-center items-center w-full h-full bg-gray-600 bg-opacity-50">
      <div className="relative p-6 mx-4 w-full max-w-md bg-white rounded-xl shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <div className="p-2 mr-3 bg-blue-100 rounded-full">
              <UsersIcon className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">
              {isEdit ? 'Edit User' : 'Add New User'}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 transition-colors hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Name */}
          <div>
            <label className="form-label">Name</label>
            <input
              type="text"
              {...register('name')}
              className={`form-input ${errors.name ? 'form-input-error' : ''}`}
              placeholder="Enter user name"
            />
            {errors.name && <p className="error-message">{errors.name.message}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="form-label">Email</label>
            <input
              type="email"
              {...register('email')}
              className={`form-input ${errors.email ? 'form-input-error' : ''} ${isEdit ? 'bg-gray-50' : ''}`}
              placeholder="Enter email address"
              readOnly={isEdit}
              disabled={isEdit}
            />
            {errors.email && <p className="error-message">{errors.email.message}</p>}
            {isEdit && (
              <p className="mt-1 text-xs text-gray-500">
                Email addresses cannot be changed for security reasons. Users can update their own
                email from their profile settings.
              </p>
            )}
          </div>

          {/* Company Name */}
          <div>
            <label className="form-label">Company Name</label>
            <input
              type="text"
              {...register('companyName')}
              className={`form-input ${errors.companyName ? 'form-input-error' : ''} ${!isSuperAdmin() ? 'bg-gray-100' : ''}`}
              placeholder="Enter company name"
              readOnly={!isSuperAdmin()}
              disabled={!isSuperAdmin()}
            />
            {errors.companyName && <p className="error-message">{errors.companyName.message}</p>}
            {!isSuperAdmin() && (
              <p className="mt-1 text-xs text-gray-500">
                New users will be added to your company account
              </p>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="form-label">Role</label>
            <select
              {...register('role')}
              className={`form-input ${errors.role ? 'form-input-error' : ''}`}
            >
              {roleOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.role && <p className="error-message">{errors.role.message}</p>}
            <div className="mt-2 space-y-1">
              {roleOptions.map(option => (
                <div key={option.value} className="text-xs text-gray-600">
                  <span className="font-medium">{option.label}:</span> {option.description}
                </div>
              ))}
            </div>
          </div>

          {/* Password */}
          {!isEdit && (
            <div>
              <label className="form-label">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  {...register('password')}
                  className={`form-input pr-10 ${errors.password ? 'form-input-error' : ''}`}
                  placeholder="Enter password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="flex absolute inset-y-0 right-0 items-center pr-3"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4 text-gray-400" />
                  ) : (
                    <Eye className="w-4 h-4 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && <p className="error-message">{errors.password.message}</p>}
            </div>
          )}

          {/* Active Status */}
          {isEdit && (
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                {...register('isActive')}
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <label htmlFor="isActive" className="block ml-2 text-sm text-gray-900">
                Active User
              </label>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-3 mt-6 sm:flex-row">
            <button
              type="submit"
              className="flex-1 px-4 py-2 font-medium text-white bg-blue-600 rounded-lg transition-colors duration-200 hover:bg-blue-700"
            >
              {isEdit ? 'Update User' : 'Create User'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 font-medium text-gray-800 bg-gray-200 rounded-lg transition-colors duration-200 hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CompanyStatusModal = ({ user, onClose, onSave }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(companyStatusSchema),
    defaultValues: { companyStatus: user?.companyStatus || 'trial' },
  });

  const statusOptions = [
    {
      value: 'disabled',
      label: 'Disabled',
      description: 'Nobody can login',
      color: 'text-red-600',
    },
    {
      value: 'trial',
      label: 'Trial',
      description: 'All functions except adding extra users',
      color: 'text-yellow-600',
    },
    {
      value: 'active',
      label: 'Active',
      description: 'All functions available',
      color: 'text-green-600',
    },
  ];

  return (
    <div className="flex overflow-y-auto fixed inset-0 z-50 justify-center items-center w-full h-full bg-gray-600 bg-opacity-50">
      <div className="relative p-6 mx-4 w-full max-w-md bg-white rounded-xl shadow-2xl">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <div className="p-2 mr-3 bg-purple-100 rounded-full">
              <Building className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Update Company Status</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-3 mb-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-700">
            Company: <strong>{user?.companyName}</strong>
          </p>
          <p className="text-sm text-blue-700">
            User: <strong>{user?.name}</strong> ({user?.email})
          </p>
        </div>

        <form onSubmit={handleSubmit(onSave)} className="space-y-4">
          <div>
            <label className="form-label">Company Status</label>
            <select
              {...register('companyStatus')}
              className={`form-input ${errors.companyStatus ? 'form-input-error' : ''}`}
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.companyStatus && (
              <p className="error-message">{errors.companyStatus.message}</p>
            )}
            <div className="mt-2 space-y-1">
              {statusOptions.map(option => (
                <div key={option.value} className="text-xs text-gray-600">
                  <span className={`font-medium ${option.color}`}>{option.label}:</span>{' '}
                  {option.description}
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3 mt-6 sm:flex-row">
            <button
              type="submit"
              className="flex-1 px-4 py-2 font-medium text-white bg-purple-600 rounded-lg transition-colors duration-200 hover:bg-purple-700"
            >
              Update Status
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 font-medium text-gray-800 bg-gray-200 rounded-lg transition-colors duration-200 hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ResetPasswordModal = ({ user, onClose, onSave }) => {
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(resetPasswordSchema),
  });

  return (
    <div className="flex overflow-y-auto fixed inset-0 z-50 justify-center items-center w-full h-full bg-gray-600 bg-opacity-50">
      <div className="relative p-6 mx-4 w-full max-w-md bg-white rounded-xl shadow-2xl">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <div className="p-2 mr-3 bg-orange-100 rounded-full">
              <Key className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Reset Password</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-3 mb-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <p className="text-sm text-yellow-700">
            Resetting password for: <strong>{user?.name}</strong> ({user?.email})
          </p>
        </div>

        <form onSubmit={handleSubmit(onSave)} className="space-y-4">
          <div>
            <label className="form-label">New Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                {...register('newPassword')}
                className={`form-input pr-10 ${errors.newPassword ? 'form-input-error' : ''}`}
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="flex absolute inset-y-0 right-0 items-center pr-3"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4 text-gray-400" />
                ) : (
                  <Eye className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </div>
            {errors.newPassword && <p className="error-message">{errors.newPassword.message}</p>}
          </div>

          <div className="flex flex-col gap-3 mt-6 sm:flex-row">
            <button
              type="submit"
              className="flex-1 px-4 py-2 font-medium text-white bg-orange-600 rounded-lg transition-colors duration-200 hover:bg-orange-700"
            >
              Reset Password
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 font-medium text-gray-800 bg-gray-200 rounded-lg transition-colors duration-200 hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showCompanyStatusModal, setShowCompanyStatusModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [companyStatusUser, setCompanyStatusUser] = useState(null);

  const { user: currentUser, isSuperAdmin, isAdmin } = usePermissions();
  const dispatch = useDispatch();

  // Track if user is in self-view mode (regular users can only see themselves)
  const isSelfViewMode =
    !isSuperAdmin() && !isAdmin() && users.length === 1 && users[0]?._id === currentUser?.id;

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getAll();
      setUsers(response.data.users || []);
    } catch (error) {
      handleApiError(error, 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async userData => {
    try {
      const response = await usersAPI.create(userData);
      toast.success(
        response.data.message ||
          'User created successfully. Login credentials have been sent to their email.'
      );
      setShowModal(false);
      fetchUsers();
    } catch (error) {
      handleApiError(error, 'Failed to create user');
    }
  };

  const handleUpdateUser = async userData => {
    try {
      await usersAPI.update(editingUser._id, userData);
      toast.success('User updated successfully');
      setShowModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      handleApiError(error, 'Failed to update user');
    }
  };

  const handleDeleteUser = async userId => {
    if (
      !window.confirm('Are you sure you want to delete this user? This action cannot be undone.')
    ) {
      return;
    }

    try {
      await usersAPI.delete(userId);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      handleApiError(error, 'Failed to delete user');
    }
  };

  const handleResetPassword = async data => {
    try {
      await usersAPI.resetPassword(resetUser._id, data.newPassword);
      toast.success('Password reset successfully');
      setShowResetModal(false);
      setResetUser(null);
    } catch (error) {
      handleApiError(error, 'Failed to reset password');
    }
  };

  const handleUpdateCompanyStatus = async data => {
    try {
      await usersAPI.updateCompanyStatus(companyStatusUser._id, data.companyStatus);
      toast.success('Company status updated successfully');
      setShowCompanyStatusModal(false);
      setCompanyStatusUser(null);
      fetchUsers();
    } catch (error) {
      handleApiError(error, 'Failed to update company status');
    }
  };

  const handleImpersonate = async user => {
    if (
      !window.confirm(
        `Are you sure you want to impersonate ${user.name}? You will be logged in as this user.`
      )
    ) {
      return;
    }

    try {
      await dispatch(impersonate(user._id)).unwrap();
      toast.success(`Now impersonating ${user.name}`);
      dispatch(reset());
      // Optionally redirect to dashboard
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error || 'Failed to start impersonation');
    }
  };

  const filteredUsers = users.filter(user => {
    // First apply search filter
    const matchesSearch =
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.companyName.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    // If current user is not super admin, hide super admin users
    if (!isSuperAdmin() && user.role === 'super-admin') {
      return false;
    }

    return true;
  });

  const getRoleIcon = role => {
    switch (role) {
      case 'super-admin':
        return <Shield className="w-4 h-4 text-purple-600" />;
      case 'admin':
        return <Shield className="w-4 h-4 text-red-600" />;
      case 'bookings':
        return <UserCheck className="w-4 h-4 text-blue-600" />;
      case 'view-only':
        return <UserX className="w-4 h-4 text-gray-600" />;
      default:
        return <UsersIcon className="w-4 h-4 text-gray-600" />;
    }
  };

  const getRoleBadgeColor = role => {
    switch (role) {
      case 'super-admin':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'bookings':
        return 'bg-blue-100 text-blue-800';
      case 'view-only':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCompanyStatusBadge = status => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded-full">
            Active
          </span>
        );
      case 'trial':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold text-yellow-800 bg-yellow-100 rounded-full">
            Trial
          </span>
        );
      case 'disabled':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold text-red-800 bg-red-100 rounded-full">
            Disabled
          </span>
        );
      default:
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 rounded-full">
            Unknown
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-12 h-12 rounded-full border-b-2 border-blue-600 animate-spin"></div>
      </div>
    );
  }

  return (
    <PermissionGuard permission="manage-users">
      <div>
        {/* Header */}
        <div className="sm:flex sm:items-center sm:justify-between">
          <div className="sm:flex-auto">
            <h2 className="text-xl font-semibold text-gray-900">
              {isSelfViewMode ? 'My Profile' : 'Users'}
            </h2>
            <p className="mt-2 text-sm text-gray-700">
              {isSelfViewMode
                ? 'View and manage your account information and settings.'
                : 'Manage user accounts and their role permissions.'}
            </p>
            {isSelfViewMode && (
              <div className="p-3 mt-2 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-700">
                  <span className="font-medium">Note:</span> You are viewing your own profile. Only
                  super-admin users and admin users can manage other user accounts.
                </p>
              </div>
            )}
          </div>
          {!isSelfViewMode && (
            <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
              <button
                onClick={() => setShowModal(true)}
                className="inline-flex justify-center items-center px-3 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg shadow-sm hover:bg-blue-500"
              >
                <Plus className="mr-2 w-4 h-4" />
                Add User
              </button>
            </div>
          )}
        </div>

        {/* Search - Only show for super-admin or when there are multiple users */}
        {(isSuperAdmin() || users.length > 1) && (
          <div className="mt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder={isSelfViewMode ? 'Search your profile...' : 'Search users...'}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="py-2 pr-4 pl-10 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="overflow-hidden mt-6 bg-white rounded-lg shadow-sm">
          {isSelfViewMode && (
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Profile View:</span> You are viewing your own account
                information.
              </p>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    User
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Role
                  </th>
                  {isSuperAdmin() && (
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Company Status
                    </th>
                  )}
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-right text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map(user => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 w-10 h-10">
                          <div className="flex justify-center items-center w-10 h-10 bg-gray-300 rounded-full">
                            <span className="text-sm font-medium text-gray-700">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.name}
                            {user._id === currentUser?.id && (
                              <span className="ml-2 text-xs text-blue-600">(You)</span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                          <div className="text-xs text-gray-400">{user.companyName}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getRoleIcon(user.role)}
                        <span
                          className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}
                        >
                          {user.roleDisplayName}
                        </span>
                      </div>
                    </td>
                    {isSuperAdmin() && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getCompanyStatusBadge(user.companyStatus)}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {user.lastLogin ? formatDateUK(user.lastLogin) : 'Never'}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                      <div className="flex justify-end items-center space-x-2">
                        {/* Edit button - available for own profile, admin for company users, or super-admin for all */}
                        {(user._id === currentUser?.id ||
                          isSuperAdmin() ||
                          (isAdmin() && user.companyName === currentUser?.companyName)) && (
                          <button
                            onClick={() => {
                              setEditingUser(user);
                              setShowModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                            title={user._id === currentUser?.id ? 'Edit your profile' : 'Edit user'}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}

                        {/* Reset password - available for own profile, admin for company users, or super-admin for all */}
                        {(user._id === currentUser?.id ||
                          isSuperAdmin() ||
                          (isAdmin() && user.companyName === currentUser?.companyName)) && (
                          <button
                            onClick={() => {
                              setResetUser(user);
                              setShowResetModal(true);
                            }}
                            className="text-orange-600 hover:text-orange-900"
                            title={
                              user._id === currentUser?.id
                                ? 'Change your password'
                                : 'Reset password'
                            }
                          >
                            <Key className="w-4 h-4" />
                          </button>
                        )}

                        {/* Super-admin only actions */}
                        {isSuperAdmin() && user.role !== 'super-admin' && (
                          <>
                            <button
                              onClick={() => {
                                setCompanyStatusUser(user);
                                setShowCompanyStatusModal(true);
                              }}
                              className="text-purple-600 hover:text-purple-900"
                              title="Update company status"
                            >
                              <Building className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleImpersonate(user)}
                              className="text-green-600 hover:text-green-900"
                              title="Impersonate user"
                            >
                              <UserCog className="w-4 h-4" />
                            </button>
                          </>
                        )}

                        {/* Delete button - super-admin can delete all, admin can delete company users, no one can delete themselves */}
                        {(isSuperAdmin() ||
                          (isAdmin() && user.companyName === currentUser?.companyName)) &&
                          user._id !== currentUser?.id && (
                            <button
                              onClick={() => handleDeleteUser(user._id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete user"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {filteredUsers.length === 0 && (
          <div className="py-12 text-center">
            <UsersIcon className="mx-auto w-12 h-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {searchTerm
                ? 'No users found'
                : isSelfViewMode
                  ? 'Profile not found'
                  : 'No users found'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm
                ? isSelfViewMode
                  ? "Your profile information doesn't match the search criteria. Try clearing the search to view your profile."
                  : 'Try adjusting your search criteria.'
                : isSelfViewMode
                  ? 'There was an issue loading your profile information.'
                  : 'Get started by adding a new user.'}
            </p>
          </div>
        )}

        {/* Modals */}
        {showModal && (
          <UserModal
            user={editingUser}
            isEdit={!!editingUser}
            onClose={() => {
              setShowModal(false);
              setEditingUser(null);
            }}
            onSave={editingUser ? handleUpdateUser : handleCreateUser}
          />
        )}

        {showResetModal && (
          <ResetPasswordModal
            user={resetUser}
            onClose={() => {
              setShowResetModal(false);
              setResetUser(null);
            }}
            onSave={handleResetPassword}
          />
        )}

        {showCompanyStatusModal && (
          <CompanyStatusModal
            user={companyStatusUser}
            onClose={() => {
              setShowCompanyStatusModal(false);
              setCompanyStatusUser(null);
            }}
            onSave={handleUpdateCompanyStatus}
          />
        )}
      </div>
    </PermissionGuard>
  );
};

export default Users;
